import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { NavItem } from '../api/alpha';
import { getOrgId, getTenantId, getWorkspaceId } from '../lib/auth-storage';
import { resolveShellNav, type ShellNavMode } from './navConfig';
import {
  getCollapsed,
  getFavoritePaths,
  getNavMode,
  getPinnedPaths,
  getRecentPaths,
  pushRecent,
  readContextLabels,
  setCollapsed as persistCollapsed,
  setNavMode as persistMode,
  toggleFavorite as persistFav,
  togglePinned as persistPin,
  writeContextLabels,
  type ShellContextSnapshot,
} from './shellPrefs';

type ShellContextValue = {
  mode: ShellNavMode;
  setMode: (m: ShellNavMode) => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
  primaryNav: NavItem[];
  allNav: NavItem[];
  pinned: string[];
  favorites: string[];
  recent: string[];
  togglePinned: (path: string) => void;
  toggleFavorite: (path: string) => void;
  context: ShellContextSnapshot;
  setContextLabels: typeof writeContextLabels;
  openCommand: boolean;
  setOpenCommand: (v: boolean) => void;
  openSearchFocus: number;
  requestSearchFocus: () => void;
  /** One refresh owner for the whole shell — pages listen, they do not add buttons. */
  refreshToken: number;
  refreshing: boolean;
  requestRefresh: () => void;
  /** Named connections published by the active page for the context strip. */
  connectionOptions: ConnectionOption[];
  setConnectionOptions: (options: ConnectionOption[]) => void;
};

export type ConnectionOption = { id: string; label: string };

const ShellCtx = createContext<ShellContextValue | null>(null);

export function ShellProvider({
  allNav,
  children,
}: {
  allNav: NavItem[];
  children: ReactNode;
}) {
  const location = useLocation();
  const [mode, setModeState] = useState<ShellNavMode>(() => getNavMode());
  const [collapsed, setCollapsedState] = useState(() => getCollapsed());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pinned, setPinned] = useState(() => getPinnedPaths());
  const [favorites, setFavorites] = useState(() => getFavoritePaths());
  const [recent, setRecent] = useState(() => getRecentPaths());
  const [labels, setLabels] = useState(() => readContextLabels());
  const [openCommand, setOpenCommand] = useState(false);
  const [openSearchFocus, setOpenSearchFocus] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [connectionOptions, setConnectionOptionsState] = useState<ConnectionOption[]>([]);

  useEffect(() => {
    setMobileOpen(false);
    setRecent(pushRecent(location.pathname));
  }, [location.pathname]);

  const setMode = useCallback((m: ShellNavMode) => {
    persistMode(m);
    setModeState(m);
  }, []);

  const setCollapsed = useCallback((v: boolean) => {
    persistCollapsed(v);
    setCollapsedState(v);
  }, []);

  const togglePinned = useCallback((path: string) => {
    setPinned(persistPin(path));
  }, []);

  const toggleFavorite = useCallback((path: string) => {
    setFavorites(persistFav(path));
  }, []);

  const setContextLabels = useCallback((partial: Parameters<typeof writeContextLabels>[0]) => {
    writeContextLabels(partial);
    setLabels(readContextLabels());
  }, []);

  const requestSearchFocus = useCallback(() => {
    setOpenSearchFocus((n) => n + 1);
  }, []);

  const requestRefresh = useCallback(() => {
    setRefreshToken((n) => n + 1);
    setRefreshing(true);
    window.setTimeout(() => setRefreshing(false), 600);
  }, []);

  const setConnectionOptions = useCallback((options: ConnectionOption[]) => {
    setConnectionOptionsState((prev) => {
      const same =
        prev.length === options.length &&
        prev.every((p, i) => p.id === options[i].id && p.label === options[i].label);
      return same ? prev : options;
    });
  }, []);

  const primaryNav = useMemo(() => resolveShellNav(allNav, mode), [allNav, mode]);

  const context = useMemo<ShellContextSnapshot>(
    () => ({
      workspaceId: getWorkspaceId(),
      tenantId: getTenantId(),
      orgId: getOrgId(),
      ...labels,
      environment: import.meta.env.MODE === 'production' ? 'production' : 'development',
    }),
    [labels, location.pathname],
  );

  const value = useMemo(
    () => ({
      mode,
      setMode,
      collapsed,
      setCollapsed,
      mobileOpen,
      setMobileOpen,
      primaryNav,
      allNav,
      pinned,
      favorites,
      recent,
      togglePinned,
      toggleFavorite,
      context,
      setContextLabels,
      openCommand,
      setOpenCommand,
      openSearchFocus,
      requestSearchFocus,
      refreshToken,
      refreshing,
      requestRefresh,
      connectionOptions,
      setConnectionOptions,
    }),
    [
      mode,
      setMode,
      collapsed,
      setCollapsed,
      mobileOpen,
      primaryNav,
      allNav,
      pinned,
      favorites,
      recent,
      togglePinned,
      toggleFavorite,
      context,
      setContextLabels,
      openCommand,
      openSearchFocus,
      requestSearchFocus,
      refreshToken,
      refreshing,
      requestRefresh,
      connectionOptions,
      setConnectionOptions,
    ],
  );

  return <ShellCtx.Provider value={value}>{children}</ShellCtx.Provider>;
}

export function useShell() {
  const v = useContext(ShellCtx);
  if (!v) throw new Error('useShell must be used within ShellProvider');
  return v;
}
